/**
 * Avatar manifest. Avatars are chosen at registration and identified by a
 * string key. Frame geometry is shared by every sheet so the client can
 * register animations generically.
 */

export type Direction = 0 | 1 | 2 | 3;
export const DIR_DOWN: Direction = 0;
export const DIR_LEFT: Direction = 1;
export const DIR_RIGHT: Direction = 2;
export const DIR_UP: Direction = 3;

export type DirectionName = "down" | "left" | "right" | "up";

export const DIRECTION_NAMES: Record<Direction, DirectionName> = {
  0: "down",
  1: "left",
  2: "right",
  3: "up",
};

export type AnimName =
  | "idle_down"
  | "idle_up"
  | "idle_left"
  | "idle_right"
  | "walk_down"
  | "walk_up"
  | "walk_left"
  | "walk_right"
  | "sit"
  | "drink";

/** Row of each animation within every avatar sheet. */
export const ANIM_ROWS: Record<AnimName, number> = {
  idle_down: 0,
  idle_up: 1,
  idle_left: 2,
  idle_right: 3,
  walk_down: 4,
  walk_up: 5,
  walk_left: 6,
  walk_right: 7,
  sit: 8,
  drink: 9,
};

export const SHEET_FRAME_W = 16;
export const SHEET_FRAME_H = 24;
export const SHEET_COLUMNS = 4;

export interface AvatarDef {
  id: string;
  label: string;
  /** Path (relative to the client `public/assets`) of the sprite sheet. */
  sheet: string;
  /** Placeholder tint used until real art is present. */
  tint: number;
  /** Hair/clothes accent used by the placeholder renderer. */
  accent: number;
}

export const AVATARS: AvatarDef[] = [
  { id: "ava_1", label: "Alex", sheet: "avatars/ava_1.png", tint: 0xe8b07a, accent: 0x2d6cdf },
  { id: "ava_2", label: "Sam", sheet: "avatars/ava_2.png", tint: 0xc98d5a, accent: 0xd94f4f },
  { id: "ava_3", label: "Jo", sheet: "avatars/ava_3.png", tint: 0xf0c9a0, accent: 0x3fae7a },
  { id: "ava_4", label: "Kai", sheet: "avatars/ava_4.png", tint: 0x9c6b47, accent: 0x8a5cf6 },
  { id: "ava_5", label: "Rio", sheet: "avatars/ava_5.png", tint: 0xd9a06b, accent: 0xe8a33d },
  { id: "ava_6", label: "Noa", sheet: "avatars/ava_6.png", tint: 0x8a5a3b, accent: 0xe4557f },
  { id: "ava_7", label: "Ivy", sheet: "avatars/ava_7.png", tint: 0xf2d3b3, accent: 0x2ba6a6 },
  { id: "ava_8", label: "Max", sheet: "avatars/ava_8.png", tint: 0x6f4a30, accent: 0x5a7d2c },
];

export const AVATAR_IDS = AVATARS.map((a) => a.id);

export function isAvatarId(value: unknown): value is string {
  return typeof value === "string" && AVATARS.some((a) => a.id === value);
}

export function getAvatar(id: string): AvatarDef {
  return AVATARS.find((a) => a.id === id) ?? AVATARS[0]!;
}

/** The animation name for a walking/idle pose in a given direction. */
export function moveAnim(dir: Direction, moving: boolean): AnimName {
  const name = DIRECTION_NAMES[dir];
  return `${moving ? "walk" : "idle"}_${name}` as AnimName;
}
